import React from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import { connect } from 'react-redux';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import {
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  parse
} from '../../util/urlHelpers';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';

import { Page } from '../../components';
import { EditItemForm } from '../../forms';
import { TopbarContainer } from '..';

import {
  requestCreateItem,
  requestUpdateItem,
  requestImageUpload,
  updateImageOrder,
  removeItemImage,
  loadData,
  requestShowProduct
} from './EditItemPage.slice';

import css from './EditItemPage.css';

const { UUID } = sdkTypes;

export const EditItemPageComponent = props => {
  const {
    getOwnItem,
    history,
    intl,
    onImageUpload,
    onRemoveItemImage,
    onUpdateImageOrder,
    page,
    params,
    scrollingDisabled,
    onCreateItem,
    onUpdateItem,
    catalogProducts,
    location,
    onShowProduct
  } = props;

  const { id, type } = params;
  const { productId } = parse(location.search);
  const isNewURI = type === LISTING_PAGE_PARAM_TYPE_NEW;
  const isDraftURI = type === LISTING_PAGE_PARAM_TYPE_DRAFT;
  const isNewListingFlow = isNewURI || isDraftURI;
  const itemId = !isNewURI && id && id !== '00000-0000-0000-0000' ? new UUID(id) : null;
  const currentListing = isNewURI ? null : getOwnItem(itemId);

  const showForm = isNewURI || currentListing?.id;

  const getRawUuid = id => {
    if (!id) return null;
    if (typeof id === 'string') return id;
    return id.uuid || id.id || null;
  };

  if (showForm) {
    const currentListingImages =
      currentListing && currentListing.images ? currentListing.images : [];

    const imageOrder = page.imageOrder || [];
    const unattachedImages = Object.keys(page.images).map(k => page.images[k]);

    const allImages = isNewURI ? unattachedImages : currentListingImages.concat(unattachedImages);

    const removedImageIds = page.removedImageIds || [];
    const removedUuids = removedImageIds.map(getRawUuid);

   const images = allImages
    .filter(img => {
      if (!img) return false;
      const imgUuid = getRawUuid(img.id);

      return !removedUuids.includes(imgUuid);
    })
    .sort((a, b) => {
      if (!a || !b) return 0;
      const aUuid = getRawUuid(a.id);
      const bUuid = getRawUuid(b.id);
      return imageOrder.indexOf(aUuid) - imageOrder.indexOf(bUuid);
    });

    const pageTitle = isNewListingFlow
      ? intl.formatMessage({ id: 'EditItemPage.titleCreateItem' })
      : intl.formatMessage({ id: 'EditItemPage.titleEditItem' });

    const buttonMsg = isNewListingFlow
      ? intl.formatMessage({ id: 'EditItemPage.add' })
      : intl.formatMessage({ id: 'EditItemPage.update' });

    const { title, description, articleCode, externalLink, shortTitle, index } = currentListing?.attributes || {};
    const { updateItemInProgress, createItemInProgress } = page;

    const redirectToCatalogWithHighlight = (productId, itemId) => {
      const routes = routeConfiguration();
      const currentSearchParams = parse(location.search);
      delete currentSearchParams.productId;
      const targetPage = currentSearchParams.page || 1;

      const catalogURI = createResourceLocatorString(
        'ConsoleProductCatalogPage', 
        routes,
        {},
        { 
          ...currentSearchParams, 
          page: targetPage, 
          highlightedProductId: productId, 
          highlightedItemId: itemId 
        }
      );
      history.push(catalogURI);
    };

    const { catalogProduct } = currentListing || {};
    const initialCatalogProduct = isNewListingFlow ? productId : catalogProduct?.id?.uuid;

    return (
      <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
        <TopbarContainer
          className={css.topbar}
          mobileRootClassName={css.mobileTopbar}
          desktopClassName={css.desktopTopbar}
          mobileClassName={css.mobileTopbar}
        />
        <div className={css.root}>
          <div className={css.headerWrapper}>
            <h1 className={css.title}>{pageTitle}</h1>
          </div>
          <EditItemForm
            className={css.form}
            initialValues={{ title, description, articleCode, externalLink, catalogProductId: initialCatalogProduct, images, index, shortTitle }}
            saveActionMsg={buttonMsg}
            images={images}
            onSubmit={values => {
              const { title, description, articleCode, externalLink, catalogProductId, images, index, shortTitle } = values;
              const updateValues = {
                title: title?.trim() || '',
                description: description || '',
                articleCode,
                externalLink,
                catalogProductId,
                imageIds: images.map(i => (typeof i.id === 'string' ? i.imageId.uuid : i.id.uuid)),
                index: parseInt(index, 10) || 0,
                shortTitle: shortTitle?.trim() || '',
              };

              if (currentListing?.id?.uuid && params.type === 'edit') {
                onUpdateItem({ id: currentListing.id.uuid, ...updateValues })
                  .then(() => onShowProduct({ id: catalogProductId, include: ['images', 'catalogItems.images'] }))
                  .then(() => redirectToCatalogWithHighlight(catalogProductId, currentListing.id.uuid));
              } else {
                onCreateItem(updateValues)
                  .then(res => {
                    const createdItemId = res.payload.data.data.id.uuid;
                    return onShowProduct({ id: catalogProductId, include: ['images', 'catalogItems.images'] })
                    .then(() => redirectToCatalogWithHighlight(catalogProductId, createdItemId))
                })
              }
            }}
            disabled={false}
            ready={false}
            updated={false}
            submitInProgress={updateItemInProgress || createItemInProgress}
            fetchErrors={null}
            catalogProducts={catalogProducts}
            onImageUpload={onImageUpload}
            onRemoveImage={onRemoveItemImage}
            isUpdate={!!currentListing?.id}
            keepDirtyOnReinitialize
            onUpdateImageOrder={onUpdateImageOrder}
          />
        </div>
      </Page>
    );
  } else {
    const loadingPageMsg = {
      id: 'EditItemPage.loadingListingData',
    };
    return (
      <Page title={intl.formatMessage(loadingPageMsg)} scrollingDisabled={scrollingDisabled} />
    );
  }
};

const mapStateToProps = state => {
  const page = state.EditItemPage;
  const { currentUser } = state.user;

  const getOwnItem = id => {
    const items = getMarketplaceEntities(state, [{ id, type: 'catalog-item' }]);
    return items.length === 1 ? items[0] : null;
  };

  return {
    currentUser,
    fetchInProgress: false,
    getOwnItem,
    page,
    scrollingDisabled: isScrollingDisabled(state),
    catalogProducts: getMarketplaceEntities(state, page.catalogProducts),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateItem: (values) => dispatch(requestUpdateItem(values)),
  onCreateItem: values => dispatch(requestCreateItem(values)),
  onImageUpload: data => dispatch(requestImageUpload(data)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onUpdateImageOrder: imageOrder => dispatch(updateImageOrder(imageOrder)),
  onRemoveItemImage: imageId => dispatch(removeItemImage(imageId)),
  onShowProduct: params => dispatch(requestShowProduct(params)),
});

const EditItemPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(injectIntl(EditItemPageComponent));

EditItemPage.loadData = loadData;

export default EditItemPage;
